import { useRef, useState, type FormEvent } from "react";

import { Dialog } from "@/components/ui/Dialog";
import {
  createMediaService,
  createTaxonomyService,
} from "@/core/services/factory";
import { promotedTypeForTaxonomyItem } from "@/core/services/taxonomy.promoted";
import type { PromotedCategory } from "@/core/services/types/promoted-category.types";
import type { TaxonomyItem } from "@/core/services/types/taxonomy.types";
import { useTranslations } from "@/lib/useTranslations";

interface PromoteCategoryDialogProps {
  open: boolean;
  item: TaxonomyItem | null;
  existing: PromotedCategory[];
  onClose: () => void;
  onSaved: (items: PromotedCategory[]) => void;
}

export function PromoteCategoryDialog({
  open,
  item,
  existing,
  onClose,
  onSaved,
}: PromoteCategoryDialogProps) {
  const t = useTranslations("taxonomy");
  const tCommon = useTranslations("common");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setError(null);
    setSaving(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFileChange = (next: File | null) => {
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(next);
    setPreviewUrl(next ? URL.createObjectURL(next) : null);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!item) return;

    const type = promotedTypeForTaxonomyItem(item);
    if (!type) {
      setError(t("promoted.unsupportedType"));
      return;
    }

    if (!file) {
      setError(t("promoted.iconRequired"));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const upload = await createMediaService().uploadTaxonomyIcon(file);
      if (!upload.success) {
        setError(t("promoted.iconUploadError"));
        return;
      }

      const nextItem: PromotedCategory = {
        category: item.label,
        img: upload.publicUrl,
        type,
      };

      const withoutDuplicate = existing.filter(
        (row) => !(row.category === nextItem.category && row.type === nextItem.type),
      );
      const next = [...withoutDuplicate, nextItem];

      const result = await createTaxonomyService().replacePromotedCategories(next);
      if (!result.success) {
        setError(result.error || t("promoted.saveError"));
        return;
      }

      onSaved(result.data);
      handleClose();
    } catch {
      setError(t("promoted.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open && item != null}
      onClose={handleClose}
      title={t("promoted.promoteTitle")}
      panelClassName="max-w-lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <p className="text-sm text-neutral-600">
          {t("promoted.promoteHelp", { label: item?.label ?? "" })}
        </p>

        <div className="rounded-xl border border-black/10 bg-zinc-50 px-3 py-2 text-sm text-whart-text">
          <span className="font-medium">{item?.label}</span>
        </div>

        <label className="block space-y-1.5">
          <span className="text-xs uppercase tracking-wider text-neutral-500">
            {t("promoted.iconLabel")}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".svg,image/svg+xml"
            onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
            className="block w-full text-sm text-neutral-600 file:mr-3 file:rounded-xl file:border-0 file:bg-yellow-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-zinc-900"
          />
          <span className="block text-xs text-neutral-500">
            {t("promoted.iconHint")}
          </span>
        </label>

        {previewUrl ? (
          <div className="flex items-center gap-2 rounded-xl border border-black/10 px-3 py-2">
            <img
              src={previewUrl}
              alt=""
              width={16}
              height={16}
              className="size-4"
            />
            <span className="text-xs text-neutral-600">{file?.name}</span>
          </div>
        ) : null}

        {error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl border border-black/10 px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-zinc-50"
          >
            {tCommon("cancel")}
          </button>
          <button
            type="submit"
            disabled={saving || !file}
            className="rounded-xl bg-yellow-100 px-3 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-yellow-200 disabled:opacity-50"
          >
            {saving ? tCommon("saving") : t("promoted.promoteSubmit")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
